import { ArrowRightIcon } from '@phosphor-icons/react';
import { Badge, Cell, Checkbox, Image, Row } from '@skupreme/shared/ui';
import { sizedImage } from '../../../../lib/listingImage';
import { whyText } from '../../lib/why-text';
import type {
  MappingSuggestionRow,
  SuggestionView,
} from '../../lib/suggestion-types';
import { ConfidenceChip, PackBadge } from './ConfidenceChip';
import { GatedButton } from './GatedButton';
import { type RowHandlers, SuggestionActions } from './SuggestionActions';

const SOURCE_KINDS = {
  marketplace: 'Marketplace',
  storage_provider: 'Storage provider',
} as const;

function ListingCell({ row }: { row: MappingSuggestionRow }) {
  return (
    <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-0.5">
      <span className="sk:flex sk:items-center sk:gap-2 sk:text-dense sk:text-fg-secondary">
        {row.connectionLabel ?? 'Unknown connection'}
        <Badge tone="neutral">{SOURCE_KINDS[row.sourceKind]}</Badge>
      </span>
      {row.sku ? (
        <span
          className="sk:truncate sk:text-sm sk:font-medium sk:text-fg"
          title={row.sku}
        >
          {row.sku}
        </span>
      ) : null}
      {row.title ? (
        <span className="sk:truncate sk:text-sm sk:text-fg" title={row.title}>
          {row.title}
        </span>
      ) : null}
      {row.gtin ? (
        <span className="sk:truncate sk:text-dense sk:text-fg-secondary">
          Barcode {row.gtin}
        </span>
      ) : null}
    </div>
  );
}

function SuggestedCell({
  row,
  handlers,
  isReadOnly,
  isBusy,
}: {
  row: MappingSuggestionRow;
  handlers: RowHandlers;
  isReadOnly?: boolean;
  isBusy?: boolean;
}) {
  const why = whyText(row.rule, row.reason, row.sku);

  if (!row.itemId) {
    return (
      <div className="sk:flex sk:flex-col sk:items-start sk:gap-1">
        <span className="sk:text-sm sk:text-fg-secondary">{why}</span>
        <div className="sk:flex sk:flex-wrap sk:gap-1">
          {row.candidates.map(candidate => (
            <GatedButton
              key={candidate.itemId}
              variant="pill"
              size="sm"
              isReadOnly={isReadOnly}
              isDisabled={isBusy}
              onPress={() => handlers.choose(row, candidate)}
            >
              {candidate.sku ?? 'Unnamed product'}
            </GatedButton>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="sk:flex sk:min-w-0 sk:items-start sk:gap-3">
      <Image
        src={sizedImage(row.itemImage, 'thumb')}
        alt=""
        size="sm"
        loading="lazy"
      />
      <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-0.5">
        <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-2">
          <span
            className="sk:truncate sk:text-sm sk:font-medium sk:text-fg"
            title={row.itemSku ?? undefined}
          >
            {row.itemSku}
          </span>
          <PackBadge pack={row.packQuantity} />
          {row.confidence === null ? null : (
            <ConfidenceChip confidence={row.confidence} />
          )}
        </span>
        <span
          className="sk:truncate sk:text-sm sk:text-fg"
          title={row.itemTitle ?? undefined}
        >
          {row.itemTitle}
        </span>
        <span
          className="sk:truncate sk:text-dense sk:text-fg-secondary"
          title={why}
        >
          {why}
        </span>
      </div>
    </div>
  );
}

export type SuggestionRowProps = {
  view: SuggestionView;
  row: MappingSuggestionRow;
  isSelected: boolean;
  onToggle: (row: MappingSuggestionRow) => void;
  handlers: RowHandlers;
  isReadOnly?: boolean;
  isActionsHidden?: boolean;
  isBusy?: boolean;
};

/** One listing, the product suggested for it (or its candidates), and what to do about it. */
export function SuggestionRow({
  view,
  row,
  isSelected,
  onToggle,
  handlers,
  isReadOnly,
  isActionsHidden,
  isBusy,
}: SuggestionRowProps) {
  return (
    <Row id={row.listingId}>
      <Cell>
        <Checkbox
          aria-label={`Select ${row.sku ?? row.title ?? 'listing'}`}
          isSelected={isSelected}
          onChange={() => onToggle(row)}
        />
      </Cell>
      <Cell>
        <ListingCell row={row} />
      </Cell>
      <Cell>
        <ArrowRightIcon size={16} aria-hidden className="sk:text-fg-tertiary" />
      </Cell>
      <Cell>
        <SuggestedCell
          row={row}
          handlers={handlers}
          isReadOnly={isReadOnly}
          isBusy={isBusy}
        />
      </Cell>
      <Cell>
        {isActionsHidden ? null : (
          <SuggestionActions
            view={view}
            row={row}
            handlers={handlers}
            isReadOnly={isReadOnly}
            isBusy={isBusy}
          />
        )}
      </Cell>
    </Row>
  );
}
